// ADR 25.9.26k §1/§3 — who the console admits, and which sections it renders,
// as pure folds rather than conditions scattered through the page.
//
// The gate and the `clients` section's predicate are separate from day one:
// O5 (Backend 25.9.26n §12) admits a verified user holding a live grant
// whatever `enable_oauth_clients` says, and such a user must not see the
// OAuth-clients section or trigger a `/oauth-clients` request. ADR 30.9.26y §2
// retired the reserved `webhooks` id: webhooks are their own route
// (`/admin/webhooks`), gated by `webhooksAdmit` below.
//
// Backend 25.9.26n §12 — Connected apps is a route with its own rail
// destination (`/admin/connected-apps`), not a section, so a grant-only user
// — admitted, with no visible section — is sent there by `AdminRoute`.

import type { FeatureFlags } from '../../api/auth'

export type AdminSectionId = 'clients'

/** Fixed order: the console's reading order. */
export const SECTION_ORDER: readonly AdminSectionId[] = ['clients']

/** What the gate and the sections read (§1), assembled by `useAdminFacts`. */
export interface AdminFacts {
  flags: FeatureFlags
  /** Backend 25.9.26n §12 — `GET /oauth-grants` answered a non-empty list; a failed read is `false`. */
  hasLiveGrant: boolean
}

/** The route's and the /account door's gate: a developer's flag, or a live grant (O5). */
export function adminAdmits(facts: AdminFacts): boolean {
  return facts.flags.enable_oauth_clients === true || facts.hasLiveGrant
}

/**
 * ADR 1.10.26n §1 — the `/account` Developer door: the console's gate on a
 * verified account. Shared by `IntegrationsSection` and the settings sidebar,
 * list and route, so the category is listed exactly when its section renders.
 * `null` facts (flags or grants still loading, or a checkout settling) admit
 * nothing. Backend 25.9.26n §12 — takes `AdminFacts`, so a grant widens it.
 */
export function integrationsAdmit(facts: AdminFacts | null, emailVerified: boolean): boolean {
  return facts !== null && adminAdmits(facts) && emailVerified
}

/**
 * 30.9.26y §1 — the webhooks destination: the developer's flag plus E1's
 * per-user flag. Never a grant (Backend 25.9.26n §12): an endpoint belongs to
 * a client, and a grant-only user owns none.
 */
export function webhooksAdmit(facts: AdminFacts): boolean {
  return facts.flags.enable_oauth_clients === true && facts.flags.enable_webhooks === true
}

/** 30.9.26ai §1 — the apps destination: the developer's flag plus A1's per-user flag; never a grant, as above. */
export function appsAdmit(facts: AdminFacts): boolean {
  return facts.flags.enable_oauth_clients === true && facts.flags.enable_apps === true
}

/**
 * 2.10.26f §2 — the builder: the developer's flag plus C1's per-user flag; never a grant alone.
 * `webhooksAdmit`'s shape, not `adminAdmits && …`: a grant-only user owns no client and could make nothing.
 */
export function builderAdmits(facts: AdminFacts): boolean {
  return facts.flags.enable_oauth_clients === true && facts.flags.enable_connections_builder === true
}

const VISIBLE: Record<AdminSectionId, (facts: AdminFacts) => boolean> = {
  clients: (facts) => facts.flags.enable_oauth_clients === true,
}

export function visibleSections(facts: AdminFacts): AdminSectionId[] {
  return SECTION_ORDER.filter((id) => VISIBLE[id](facts))
}
